import {useCallback, useEffect, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {ContentHotel} from '@findhotel/sapi'
import {generateNavigationUrl} from 'components/data/WithUrlUpdater'
import {HotelOfferEntity, SearchOffer} from 'modules/sapiSearch/slice'
import {droppedMetaLandingParams} from 'modules/search/constants'

import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {
  clearPriceTrendsTrackingContext,
  setPriceTrendsTrackingContext
} from '@vio/shared/src/priceWatch/slice'

import {BetterValueHotel} from '../components/BetterValueCarousel/types'
import {PriceTrendsParams} from '../services/priceInsightsApi'
import {PriceTrendContent, PriceTrendsTrackingContext} from '../types'
import {useBetterValueHotels} from './useBetterValueHotels'
import {usePriceHistory} from './usePriceHistory'

interface UsePriceTrendPanelDataParams {
  fetchParams: Partial<PriceTrendsParams>
  isOpen: boolean
  content: PriceTrendContent | null
  /**
   * Current hotel's NIGHTLY price, fed to the better-value carousel filter
   * which compares against each candidate's `cheapestOffer.nightlyRate`.
   * The display-side `offerPrice` (can be a per-stay total when the user
   * has `showTotalPrices` on) is intentionally NOT used here — passing a
   * total would let pricier hotels through on multi-night stays.
   */
  currentNightlyPrice: number | null
  hotel: ContentHotel | undefined
  priceTrendsContext?: PriceTrendsTrackingContext
  /**
   * Whether the "Our advice" variant content is rendered. Gates the
   * anchor-safe carousel navigation so control card taps keep production
   * behavior.
   */
  isAdviceVariant?: boolean
  onViewed?: () => void
  onCarouselHotelClicked?: (
    hotel: BetterValueHotel,
    position: number,
    offer: SearchOffer,
    offerEntity: HotelOfferEntity
  ) => void
  onCarouselSeeMoreClicked?: () => void
}

export const usePriceTrendPanelData = ({
  fetchParams,
  isOpen,
  content,
  currentNightlyPrice,
  hotel,
  priceTrendsContext,
  isAdviceVariant = false,
  onViewed,
  onCarouselHotelClicked,
  onCarouselSeeMoreClicked
}: UsePriceTrendPanelDataParams) => {
  const {push} = useRouterProvider()
  const dispatch = useDispatch()

  // Store priceTrendsContext in Redux so any toggle instance can access it
  useEffect(() => {
    if (isOpen && priceTrendsContext) {
      dispatch(setPriceTrendsTrackingContext(priceTrendsContext))
    }
    return () => {
      dispatch(clearPriceTrendsTrackingContext())
    }
  }, [isOpen, priceTrendsContext, dispatch])

  const {
    priceHistory,
    latestDetailedPriceForDisplay: latestDetailedPrice,
    isLoading: isPriceHistoryLoading
  } = usePriceHistory({
    ...fetchParams,
    enabled: isOpen
  })

  const isHigherThanSimilar =
    content?.recommendation.messageKey === 'recommendationHigherThanSimilar'

  const {
    betterValueHotels,
    offersData,
    isLoading: isBetterValueLoading
  } = useBetterValueHotels({
    similarHotelsIds: priceHistory?.similarHotelsIds,
    enabled: isOpen && isHigherThanSimilar,
    checkIn: fetchParams?.checkIn,
    checkOut: fetchParams?.checkOut,
    rooms: fetchParams?.rooms,
    currency: fetchParams?.currency,
    // Always feed the nightly price here — useBetterValueHotels' filter
    // compares against each candidate's nightly rate, so passing the
    // per-stay total (when showTotalPrices is on) would let costlier
    // hotels through whose nightly happens to be below current's total.
    offerPrice: currentNightlyPrice ?? undefined
  })

  const locationName = hotel?.navPathInfo?.city?.name || ''

  const isCarouselDataReady = !isPriceHistoryLoading && !isBetterValueLoading
  const shouldHideOfferButton =
    isHigherThanSimilar &&
    (!isCarouselDataReady || betterValueHotels.length > 0)

  // Anchored SRP URL for a better-value hotel (variant only). Shared by the
  // card tap and "View alternatives" so both land on the same SRP.
  // mergeWithCurrentParams: false so carried-over filters can't hide the
  // anchor (see useHideAnchorHotel).
  const buildAnchorSrpUrl = useCallback(
    (anchorHotelId: string) =>
      generateNavigationUrl(
        {
          hotelId: anchorHotelId,
          checkIn: fetchParams?.checkIn,
          checkOut: fetchParams?.checkOut,
          rooms: fetchParams?.rooms,
          currency: fetchParams?.currency
        },
        {mergeWithCurrentParams: false}
      ),
    [fetchParams]
  )

  const handleBetterValueHotelClick = useCallback(
    (clickedHotel: BetterValueHotel, position: number) => {
      const offerEntity = offersData?.[clickedHotel.hotelId]
      const offer = offerEntity?.offers?.[0]

      if (offer && offerEntity) {
        onCarouselHotelClicked?.(clickedHotel, position, offer, offerEntity)
      }

      const url = isAdviceVariant
        ? buildAnchorSrpUrl(clickedHotel.hotelId)
        : generateNavigationUrl({
            hotelId: clickedHotel.hotelId,
            ...droppedMetaLandingParams,
            checkIn: fetchParams?.checkIn,
            checkOut: fetchParams?.checkOut,
            rooms: fetchParams?.rooms
          })
      push(url)
    },
    [
      push,
      isAdviceVariant,
      buildAnchorSrpUrl,
      fetchParams,
      onCarouselHotelClicked,
      offersData
    ]
  )

  const handleSeeMoreClick = useCallback(() => {
    onCarouselSeeMoreClicked?.()
    const url = generateNavigationUrl({
      placeId: hotel?.navPathInfo?.city?.id,
      ...droppedMetaLandingParams,
      checkIn: fetchParams?.checkIn,
      checkOut: fetchParams?.checkOut,
      rooms: fetchParams?.rooms
    })
    push(url)
  }, [
    push,
    fetchParams,
    hotel?.navPathInfo?.city?.id,
    onCarouselSeeMoreClicked
  ])

  const handleViewAlternatives = useCallback(() => {
    const firstHotel = betterValueHotels[0]
    if (!firstHotel) return
    push(buildAnchorSrpUrl(firstHotel.hotelId))
  }, [push, buildAnchorSrpUrl, betterValueHotels])

  const hasTrackedViewedRef = useRef(false)
  useEffect(() => {
    if (isOpen && !hasTrackedViewedRef.current) {
      hasTrackedViewedRef.current = true
      onViewed?.()
    }
    if (!isOpen) {
      hasTrackedViewedRef.current = false
    }
  }, [isOpen, onViewed])

  return {
    priceHistory,
    latestDetailedPrice,
    isPriceHistoryLoading,
    betterValueHotels,
    isBetterValueLoading,
    isHigherThanSimilar,
    shouldHideOfferButton,
    locationName,
    handleBetterValueHotelClick,
    handleSeeMoreClick,
    handleViewAlternatives
  }
}
